import type {
  AvailabilityStatus,
  Provider,
  ProviderInput,
} from '@psp-aggregator/sdk';
import sdk from '@psp-aggregator/sdk';
import { useMutation, useQueryClient } from '@tanstack/react-query';

const invalidateProviders = (
  queryClient: ReturnType<typeof useQueryClient>,
): void => {
  void queryClient.invalidateQueries({ queryKey: ['listProviders'] });
};

export const useCreateProviderMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: ProviderInput) =>
      sdk.requests.createProvider(payload),
    onSuccess: () => {
      invalidateProviders(queryClient);
    },
  });
};

export const useUpdateProviderMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      payload,
      providerId,
    }: {
      payload: ProviderInput;
      providerId: string;
    }) => sdk.requests.updateProvider(payload, providerId),
    onSuccess: () => {
      invalidateProviders(queryClient);
    },
  });
};

export const useDeleteProviderMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (providerId: string) => sdk.requests.deleteProvider(providerId),
    onSuccess: () => {
      invalidateProviders(queryClient);
    },
  });
};

export const useRestoreProviderMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (providerId: string) =>
      sdk.requests.restoreProvider(providerId),
    onSuccess: () => {
      invalidateProviders(queryClient);
    },
  });
};

export const useSetProviderStatusMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      providerId,
      status,
    }: {
      providerId: string;
      status: AvailabilityStatus;
    }) => sdk.requests.setProviderStatus({ status }, providerId),
    onSuccess: () => {
      invalidateProviders(queryClient);
      void queryClient.invalidateQueries({
        queryKey: ['getProviderAvailabilityImpact'],
      });
    },
  });
};

export const buildProviderUpdatePayload = (
  provider: Provider,
  values: ProviderInput,
): ProviderInput => ({
  ...values,
  private_attributes: provider.private_attributes,
  support_contacts: provider.support_contacts,
});
